import React, { useEffect, useState } from 'react';
import socket from '../config/socket';

/**
 * Map-Veto.
 *
 * Erscheint bei allen gleichzeitig, sobald der Admin auf Ready stellt. Klicken
 * darf nur der Kapitaen, der gerade dran ist -- der Server prueft das ohnehin
 * nochmal, hier geht es nur darum, niemanden ins Leere klicken zu lassen.
 *
 * Das Fenster laesst sich schliessen und schrumpft dann auf eine schmale
 * Leiste. Wichtig, weil es sonst dauerhaft ueber der Seite liegt: nach dem
 * Veto kaeme man weder an "Connect to server" noch an die Admin-Knoepfe.
 * Wer als Kapitaen an die Reihe kommt, dem geht es automatisch wieder auf.
 */

const mapLabel = (map) => map.replace(/^de_/, '').replace(/^./, (c) => c.toUpperCase());

const MapVeto = ({ veto, identity, teamNames, isAdmin }) => {
  const running = !!(veto && veto.running);
  const winner = veto ? veto.winner : null;
  const turn = veto ? veto.turn : null;
  const captains = veto ? veto.captains : null;
  const banCount = veto && veto.bans ? veto.bans.length : 0;

  const captainOnTurn = running && captains ? captains[turn] : null;
  const myTurn = !!(
    running &&
    identity &&
    identity.role === 'player' &&
    captainOnTurn &&
    captainOnTurn.playerId === identity.playerId
  );

  const [secondsLeft, setSecondsLeft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [minimized, setMinimized] = useState(false);

  // Countdown lokal herunterzaehlen; massgeblich ist der Zeitpunkt vom Server.
  const endsAt = veto ? veto.endsAt : null;
  useEffect(() => {
    if (!running || !endsAt) {
      setSecondsLeft(null);
      return undefined;
    }

    const tick = () => setSecondsLeft(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));

    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [running, endsAt]);

  // Nach jedem Bann die Fehlermeldung wegraeumen.
  useEffect(() => setError(''), [banCount]);

  // Neue Runde oder Ergebnis: wieder aufklappen.
  useEffect(() => setMinimized(false), [running, winner]);

  // Wer dran ist, soll es nicht verpassen.
  useEffect(() => {
    if (myTurn) setMinimized(false);
  }, [myTurn]);

  if (!veto || (!running && !winner)) return null;

  const nameOf = (teamNo) => (teamNo === 0 ? teamNames[0] : teamNames[1]);
  const canClick = running && (myTurn || isAdmin);

  const ban = (map) => {
    if (!canClick || busy) return;

    setBusy(true);
    setError('');

    socket.emit('veto:ban', map, (result) => {
      setBusy(false);
      if (!result || !result.ok) {
        setError(
          result && result.reason === 'not-your-turn'
            ? 'Du bist gerade nicht dran.'
            : 'Bann nicht moeglich.'
        );
      }
    });
  };

  /* Zusammengeklappt: schmale Leiste unten, der Rest der Seite ist bedienbar. */
  if (minimized) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-4">
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="flex items-center gap-3 rounded-full bg-gray-900 px-5 py-3 text-sm text-white shadow-lg hover:bg-gray-800"
        >
          {winner ? (
            <span>
              Gespielt wird <span className="font-semibold">{mapLabel(winner)}</span>
            </span>
          ) : (
            <span>
              Map-Veto laeuft
              {captainOnTurn ? ` - ${captainOnTurn.playerName} ist dran` : ''}
              {secondsLeft !== null ? ` (${secondsLeft}s)` : ''}
            </span>
          )}
          <span className="text-gray-400">oeffnen</span>
        </button>
      </div>
    );
  }

  const bannedBy = {};
  (veto.bans || []).forEach((b) => {
    bannedBy[b.map] = b;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-900/60 p-4 sm:p-8">
      <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl sm:p-8">
        {winner ? (
          <>
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-2xl font-bold">Gespielt wird</h2>
              <button
                type="button"
                onClick={() => setMinimized(true)}
                className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                Schliessen
              </button>
            </div>
            <p className="pt-6 pb-2 text-4xl font-bold text-lime-600">{mapLabel(winner)}</p>
            <p className="pb-6 text-sm text-gray-500">Alle anderen Maps wurden gebannt.</p>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-2xl font-bold">Map-Veto</h2>
              <div className="flex items-center gap-4">
                {secondsLeft !== null && (
                  <span
                    className={
                      'text-2xl font-bold tabular-nums ' +
                      (secondsLeft <= 5 ? 'text-red-600' : 'text-gray-400')
                    }
                  >
                    {secondsLeft}s
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setMinimized(true)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
                >
                  Schliessen
                </button>
              </div>
            </div>

            <p className="pt-2 pb-6 text-gray-600">
              {captainOnTurn ? (
                <>
                  <span className="font-semibold">{captainOnTurn.playerName}</span> bannt fuer{' '}
                  <span className="font-semibold">{nameOf(turn)}</span>
                </>
              ) : (
                <>
                  <span className="font-semibold">{nameOf(turn)}</span> ist dran -- kein Kapitaen
                  angemeldet, der Admin waehlt.
                </>
              )}
            </p>

            {myTurn && (
              <p className="mb-4 rounded-lg bg-lime-50 px-4 py-3 text-sm text-lime-900">
                Du bist Kapitaen und dran. Waehle eine Map zum Bannen.
              </p>
            )}
          </>
        )}

        {error && (
          <p className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
        )}

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {veto.maps.map((map) => {
            const banned = bannedBy[map];
            const isWinner = winner === map;

            return (
              <button
                key={map}
                type="button"
                disabled={!!banned || !canClick || busy || !!winner}
                onClick={() => ban(map)}
                className={
                  'flex items-center justify-between rounded-lg border px-4 py-3 text-left transition ' +
                  (isWinner
                    ? 'border-lime-400 bg-lime-50 font-semibold'
                    : banned
                    ? 'border-gray-200 bg-gray-50 text-gray-400 line-through'
                    : canClick
                    ? 'border-gray-300 bg-white hover:border-red-400 hover:bg-red-50'
                    : 'border-gray-200 bg-white')
                }
              >
                <span>{mapLabel(map)}</span>
                {banned && (
                  <span className="ml-3 shrink-0 text-xs no-underline">
                    {banned.actor === 'auto' ? 'Zeit abgelaufen' : banned.actor}
                  </span>
                )}
                {isWinner && <span className="ml-3 shrink-0 text-xs">gewaehlt</span>}
              </button>
            );
          })}
        </div>

        {running && captains && (
          <p className="pt-6 text-sm text-gray-500">
            Kapitaene: {captains[0] ? captains[0].playerName : '-'} ({teamNames[0]}) und{' '}
            {captains[1] ? captains[1].playerName : '-'} ({teamNames[1]})
          </p>
        )}
      </div>
    </div>
  );
};

export default MapVeto;
